/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import parse, { domToReact } from 'html-react-parser';
import { mdRules } from '../../content/mardownRules';
import { ezMarkdownParser } from '../../utils/parserMarkdown/markdownEngine';
import { useDispatch, useSelector } from 'react-redux';
import { setDrawerOpen, setOpenModales } from '../../store/global';
import { deepCloneArray, findIndexOfObjectInArray } from '../../utils/array';
import { update } from '../../services/item';
import { useWindowSize } from '../../hooks/useWindowSize';
import { isEditable } from '../../utils/roles';
import { Checkbox } from '../form/checkbox';
import { SettingsSelect } from '../form/settings.Widget';
import { TooltipHtml } from '../tooltip/tooltip.Html';
import { ItemShort } from '../item/item.Short';
import { useDoubleTap } from 'use-double-tap';
import {
  evaluateConditionExpression,
  evaluateMathExpression,
  processNum,
} from '../../utils/num';
import { canEditItemForItem, getId } from '../../utils/item';
import { getColorConfig } from '../../utils/parserMarkdown/mdTheme';
import { renderIconBySlug } from '../../content/icons';

const applyBaseMdStyles = html => {
  if (typeof html !== 'string') {
    return '';
  }
  let result = html;
  result = applyCodeBlockStyles(result);
  result = applyInlineCodeStyles(result);
  return result;
};

const codeWrapperBaseClass = 'bg-opacity-50 rounded shadow px-05 py-03 my-03';
const inheritFontStyle = 'font-family: inherit;';

const ensureInlineFont = attrs => {
  const addition = inheritFontStyle;
  const normalized = attrs ? attrs.trim() : '';
  if (!normalized) {
    return ` style="${addition}"`;
  }
  const styleMatch = normalized.match(/style="([^"]*)"/);
  if (styleMatch) {
    if (styleMatch[1]?.includes('font-family')) {
      return ` ${normalized}`;
    }
    const updatedStyle = `${styleMatch[1]} ${addition}`.trim();
    const updatedAttrs = normalized.replace(
      styleMatch[0],
      `style="${updatedStyle}"`
    );
    return ` ${updatedAttrs}`;
  }
  return ` ${normalized} style="${addition}"`;
};

const applyCodeBlockStyles = html => {
  return html.replace(
    /<pre><code([^>]*)>([\s\S]*?)<\/code><\/pre>(\s*\{\s*([A-Za-z]+)\s*\})?/g,
    (_, codeAttrs = '', codeContent = '', _tokenBlock, tokenRaw) => {
      const color = getColorConfig(tokenRaw);
      const bgClass = color ? color.codeBgClass : 'bg-main-d';
      const finalClass = `${bgClass} ${codeWrapperBaseClass}`;
      const codeAttributes = ensureInlineFont(
        `${codeAttrs} data-block-code="true"`.trim()
      );
      return `<div class="${finalClass}"><code${codeAttributes}>${codeContent}</code></div>`;
    }
  );
};

const applyInlineCodeStyles = html => {
  return html.replace(
    /<code(?![^>]*data-block-code="true")([^>]*)>([\s\S]*?)<\/code>(\s*\{\s*([A-Za-z]+)\s*\})?/g,
    (_, attrs = '', content = '', _tokenBlock, tokenRaw) => {
      const color = getColorConfig(tokenRaw);
      const bgClass = color ? color.codeBgClass : 'bg-main-d';
      const finalClass = `${bgClass} ${codeWrapperBaseClass}`;
      const codeAttributes = ensureInlineFont(attrs);
      return `<div class="${finalClass}"><code${codeAttributes}>${content}</code></div>`;
    }
  );
};

const decodeCheckboxAttrValue = value => {
  if (!value) {
    return '';
  }
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch (error) {
    decoded = value;
  }
  if (decoded.includes('Æ') || decoded.includes('•')) {
    decoded = decoded.replaceAll(/Æ/g, '"').replaceAll(/•/g, '');
  }
  return decoded;
};

const decodeSelectAttrValue = value => {
  if (!value) {
    return '';
  }
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

const replaceNthMdSelectToken = (
  markdown,
  targetIndex,
  nextValue,
  expectedTokenBody
) => {
  if (typeof markdown !== 'string' || markdown.length === 0) {
    return markdown;
  }
  if (typeof targetIndex !== 'number' || targetIndex < 0) {
    return markdown;
  }
  const normalizedNext = `${nextValue ?? ''}`;
  if (!normalizedNext) {
    return markdown;
  }

  let seen = 0;
  let replaced = false;
  const result = markdown.replace(/\{\{select:([\s\S]*?)\}\}/g, (full, body) => {
    if (replaced) {
      return full;
    }
    if (seen !== targetIndex) {
      seen += 1;
      return full;
    }
    const raw = `${body ?? ''}`;
    if (expectedTokenBody && raw !== expectedTokenBody) {
      // Index mismatch (or token edited manually). Keep as-is and let the caller fallback.
      seen += 1;
      return full;
    }
    const parts = raw.split('|');
    const options = parts.slice(1);
    if (options.length === 0) {
      // Invalid token, keep as-is.
      seen += 1;
      return full;
    }
    replaced = true;
    seen += 1;
    return `{{select:${normalizedNext}|${options.join('|')}}}`;
  });

  return result;
};

export const TextMd = props => {
  const { str, textId, itemId, getTextHeight, boardTabId, gridLayoutKey, style } = props;
  const [mdValue, setMdValue] = useState('');
  const item = useSelector(state => state.items[itemId]);
  const items = useSelector(state => state.items);
  const dispatch = useDispatch();
  const windowSize = useWindowSize();
  const sanitizeTokenPart = part => {
    if (typeof part !== 'string') {
      return '';
    }
    return part.trim();
  };

  const computeTokenValue = token => {
    const cleaned = token?.replace('[', '').replace(']', '');
    if (!cleaned) {
      return token;
    }
    const rawParts = cleaned.split('.');
    const useMod = rawParts.includes('useMod');
    const useCon = rawParts.includes('useCon');
    const filteredParts = rawParts.filter(
      part => part !== 'useMod' && part !== 'useCon'
    );
    if (filteredParts.length < 2) {
      return token;
    }

    if (filteredParts[0] === 'filter' && filteredParts.length >= 4) {
      const filterId = sanitizeTokenPart(filteredParts[1]);
      const op = sanitizeTokenPart(filteredParts[2]);
      const numRef = sanitizeTokenPart(filteredParts[3]);

      const filterEntry = Array.isArray(item?.filters)
        ? item.filters.find(filterObj => getId(filterObj) === filterId)
        : null;
      const filter = filterEntry ? filterEntry?.[filterId] : null;
      if (!filter || !Array.isArray(filter?.items) || filter.items.length === 0) {
        return token;
      }

      const required = filter.items.filter(Boolean);
      const excluded = Array.isArray(filter?.exclude) ? filter.exclude.filter(Boolean) : [];
      const requiredSet = new Set(required);
      const excludedSet = new Set(excluded);
      const candidateIds = [];

      Object.values(items || {}).forEach(candidate => {
        if (!candidate?._id || candidate.__partial === true) return;
        if (item?.universe && candidate?.universe && candidate.universe !== item.universe) return;
        if (!Array.isArray(candidate?.links) || candidate.links.length === 0) return;
        const linkIds = candidate.links.map(obj => getId(obj)).filter(Boolean);
        const linkSet = new Set(linkIds);
        for (const reqId of requiredSet) {
          if (!linkSet.has(reqId)) return;
        }
        for (const exId of excludedSet) {
          if (linkSet.has(exId)) return;
        }
        candidateIds.push(candidate._id);
      });

      if (candidateIds.length === 0) {
        return 0;
      }

      const noteEnabled = filter?.noteNumEnabled === true;
      const noteMap =
        filter?.noteNums && typeof filter.noteNums === 'object'
          ? filter.noteNums
          : {};

      let sum = 0;
      let foundAny = false;
      for (const id of candidateIds) {
        const candidate = items?.[id];
        if (!candidate?.has_num) continue;
        let numIndex = findIndexOfObjectInArray(candidate.has_num, numRef);
        if (numIndex === -1) {
          numIndex = candidate.has_num.findIndex(numObj => {
            const nId = getId(numObj);
            const n = numObj?.[nId];
            const label = typeof n?.label === 'string' ? n.label.trim() : '';
            return label !== '' && label === numRef;
          });
        }
        if (numIndex === -1) continue;
        const numEntry = candidate.has_num[numIndex];
        const nId = getId(numEntry);
        const n = numEntry?.[nId];
        const v = processNum({ num: n, numId: nId, useMod, useCon }, candidate, items);
        if (typeof v !== 'number' || !Number.isFinite(v)) continue;
        if (op === 'sumq' || op === 'sumWeighted') {
          const qty = noteEnabled
            ? Number.isFinite(Number(noteMap?.[id]))
              ? Number(noteMap[id])
              : 1
            : 1;
          sum += v * qty;
        } else {
          sum += v;
        }
        foundAny = true;
      }

      return foundAny ? sum : token;
    }

    // [itemId.links.constitution] => sum across current item's links that ALSO have a link to itemId.
    if (filteredParts.length >= 3 && filteredParts[1] === 'links') {
      const itemRefRaw = sanitizeTokenPart(filteredParts[0]);
      const numRef = sanitizeTokenPart(filteredParts[2]);
      const objectIdLikeRegex = /^[a-fA-F0-9]{24}$/;

      const linkIds = Array.isArray(item?.links)
        ? item.links.map(obj => getId(obj)).filter(Boolean)
        : [];
      const requiredLinkId =
        itemRefRaw === 'current'
          ? item?._id
          : items?.[itemRefRaw]?._id ||
            (objectIdLikeRegex.test(itemRefRaw) ? itemRefRaw : null);

      if (!requiredLinkId) {
        return token;
      }

      const candidateIds = linkIds.filter(linkId => {
        const linkedItem = items?.[linkId];
        if (!linkedItem?.links) return false;
        const linkedLinkIds = linkedItem.links.map(obj => getId(obj)).filter(Boolean);
        return linkedLinkIds.includes(requiredLinkId);
      });
      let sum = 0;
      let foundAny = false;

      for (const id of candidateIds) {
        const linkedItem = items?.[id];
        if (!linkedItem?.has_num) continue;

        let numIndex = findIndexOfObjectInArray(linkedItem.has_num, numRef);
        if (numIndex === -1) {
          numIndex = linkedItem.has_num.findIndex(numObj => {
            const numId = getId(numObj);
            const num = numObj?.[numId];
            const label =
              typeof num?.label === 'string' ? num.label.trim() : '';
            return label !== '' && label === numRef;
          });
        }
        if (numIndex === -1) continue;

        const numEntry = linkedItem.has_num[numIndex];
        const numId = getId(numEntry);
        const numPayload = numEntry?.[numId];
        if (!numPayload) continue;

        const resolvedValue = processNum(
          { num: numPayload, numId, useMod, useCon },
          linkedItem,
          items
        );
        if (
          typeof resolvedValue === 'number' &&
          Number.isFinite(resolvedValue)
        ) {
          sum += resolvedValue;
          foundAny = true;
        }
      }

      return foundAny ? sum : token;
    }

    const itemRef = sanitizeTokenPart(filteredParts[0]);
    const numRef = sanitizeTokenPart(filteredParts[1]);

    if (itemRef === 'links') {
      const linkIds = Array.isArray(item?.links)
        ? item.links.map(obj => getId(obj)).filter(Boolean)
        : [];
      let sum = 0;
      let foundAny = false;
      for (const linkId of linkIds) {
        const linkedItem = items?.[linkId];
        if (!linkedItem?.has_num) {
          continue;
        }
        let numIndex = findIndexOfObjectInArray(linkedItem.has_num, numRef);
        if (numIndex === -1) {
          numIndex = linkedItem.has_num.findIndex(numObj => {
            const numId = getId(numObj);
            const num = numObj?.[numId];
            const label = typeof num?.label === 'string' ? num.label.trim() : '';
            return label !== '' && label === numRef;
          });
        }
        if (numIndex === -1) {
          continue;
        }
        const numEntry = linkedItem.has_num[numIndex];
        const numId = getId(numEntry);
        const numPayload = numEntry?.[numId];
        if (!numPayload) {
          continue;
        }
        const resolvedValue = processNum(
          { num: numPayload, numId, useMod, useCon },
          linkedItem,
          items
        );
        if (typeof resolvedValue === 'number' && Number.isFinite(resolvedValue)) {
          sum += resolvedValue;
          foundAny = true;
        }
      }
      return foundAny ? sum : token;
    }

    if (itemRef === 'notes') {
      const trimmed = typeof numRef === 'string' ? numRef.trim() : '';
      if (!trimmed) {
        return token;
      }
      const objectIdLikeRegex = /^[a-fA-F0-9]{24}$/;
      const targetIds = [];
      if (objectIdLikeRegex.test(trimmed)) {
        targetIds.push(trimmed);
      } else {
        const ids = new Set();
        // Filters notes
        if (Array.isArray(item?.filters)) {
          item.filters.forEach(filterObj => {
            const filterId = getId(filterObj);
            const filter = filterObj?.[filterId];
            if (!filter || filter.noteNumEnabled !== true) return;
            const map =
              filter.noteNums && typeof filter.noteNums === 'object'
                ? filter.noteNums
                : null;
            if (!map) return;
            Object.keys(map).forEach(itemId => {
              const linked = items?.[itemId];
              const linkedLabel =
                typeof linked?.label === 'string' ? linked.label.trim() : '';
              if (linkedLabel && linkedLabel === trimmed) {
                ids.add(itemId);
              }
            });
          });
        }
        // Reduced item widgets notes (tab grids)
        if (Array.isArray(item?.tabs)) {
          item.tabs.forEach(tabObj => {
            const tabId = getId(tabObj);
            const tabItem = items?.[tabId];
            if (!Array.isArray(tabItem?.grid)) return;
            tabItem.grid.forEach(gridObj => {
              const widgetId = getId(gridObj);
              if (!widgetId || !objectIdLikeRegex.test(widgetId)) return;
              const cfg = gridObj?.[widgetId];
              if (!cfg || cfg.noteNumEnabled !== true) return;
              const widgetItem = items?.[widgetId];
              const widgetLabel =
                typeof widgetItem?.label === 'string'
                  ? widgetItem.label.trim()
                  : '';
              if (widgetLabel && widgetLabel === trimmed) {
                ids.add(widgetId);
              }
            });
          });
        }
        ids.forEach(id => targetIds.push(id));
      }

      let sum = 0;
      let foundAny = false;

      // Filters notes
      if (Array.isArray(item?.filters)) {
        item.filters.forEach(filterObj => {
          const filterId = getId(filterObj);
          const filter = filterObj?.[filterId];
          if (!filter || filter.noteNumEnabled !== true) return;
          const map =
            filter.noteNums && typeof filter.noteNums === 'object'
              ? filter.noteNums
              : null;
          if (!map) return;
          targetIds.forEach(id => {
            const val = Number(map?.[id]);
            if (Number.isFinite(val)) {
              sum += val;
              foundAny = true;
            }
          });
        });
      }

      // Reduced item widgets notes
      if (Array.isArray(item?.tabs)) {
        item.tabs.forEach(tabObj => {
          const tabId = getId(tabObj);
          const tabItem = items?.[tabId];
          if (!Array.isArray(tabItem?.grid)) return;
          tabItem.grid.forEach(gridObj => {
            const widgetId = getId(gridObj);
            if (!widgetId || !targetIds.includes(widgetId)) return;
            const cfg = gridObj?.[widgetId];
            if (!cfg || cfg.noteNumEnabled !== true) return;
            const val = Number(cfg.noteNum);
            if (Number.isFinite(val)) {
              sum += val;
              foundAny = true;
            }
          });
        });
      }

      return foundAny ? sum : token;
    }

    const targetItem =
      itemRef === 'current' ? item : itemRef ? items?.[itemRef] : null;
    if (!targetItem?.has_num) {
      return token;
    }
    let numIndex = findIndexOfObjectInArray(targetItem.has_num, numRef);
    if (numIndex === -1) {
      // Fallback: allow referencing nums by label: [itemId.numLabel]
      numIndex = targetItem.has_num.findIndex(numObj => {
        const numId = getId(numObj);
        const num = numObj?.[numId];
        const label = typeof num?.label === 'string' ? num.label.trim() : '';
        return label !== '' && label === numRef;
      });
    }
    if (numIndex === -1) {
      return token;
    }
    const numEntry = targetItem.has_num[numIndex];
    const numId = getId(numEntry);
    const numPayload = numEntry?.[numId];
    if (!numPayload) {
      return token;
    }
    const resolvedValue = processNum(
      { num: numPayload, numId, useMod, useCon },
      targetItem,
      items
    );
    return Number.isFinite(resolvedValue) ? resolvedValue : token;
  };

  const injectNumValues = text => {
    if (typeof text !== 'string') {
      return text;
    }
    // Only replace numeric tokens (and avoid markdown link labels: [label](url)).
    const tokenRegex = /\[[^[\]]+\.[^[\]]+\](?!\s*\()/g;
    return text.replace(tokenRegex, match => computeTokenValue(match));
  };

  const resolveTernaries = text => {
    if (typeof text !== 'string') {
      return text;
    }
    const ternaryRegex = /\{\{([^{}]+?)\?\s*([^{}]*?)\s*:\s*([^{}]*?)\}\}/g;
    return text.replace(ternaryRegex, (_, conditionPart, truePart, falsePart) => {
      const matchesCondition = evaluateConditionExpression(
        conditionPart.trim(),
        item,
        items
      );
      return (matchesCondition ? truePart : falsePart).trim();
    });
  };

  const resolveCalculations = text => {
    if (typeof text !== 'string') {
      return text;
    }
    const calcRegex = /\{\{=\s*([^{}]+?)\s*\}\}/g;
    return text.replace(calcRegex, (_, expressionPart) => {
      const trimmedExpression = expressionPart?.trim();
      if (!trimmedExpression) {
        return '';
      }
      const result = evaluateMathExpression(trimmedExpression, item, items);
      if (typeof result === 'number' && Number.isFinite(result)) {
        return `${result}`;
      }
      return '';
    });
  };

  const renderMd = () => {
    let strToConvert = str;
    if (strToConvert) {
      strToConvert = resolveTernaries(strToConvert);
      strToConvert = injectNumValues(strToConvert);
      strToConvert = resolveCalculations(strToConvert);
      const htmlValue = ezMarkdownParser(strToConvert);
      const nextValue = applyBaseMdStyles(htmlValue);
      setMdValue(prev => (prev === nextValue ? prev : nextValue));
    }
  };

  const hasDynamicMarkdown = useMemo(() => {
    if (typeof str !== 'string') {
      return false;
    }
    const hasNumToken = /\[[^\]]+\.[^\]]+\]/.test(str);
    // Dynamic blocks that depend on item/items values.
    return (
      hasNumToken ||
      str.includes('[[') ||
      str.includes('{{') ||
      str.includes('[filter.') ||
      str.includes('[links.') ||
      str.includes('[current.')
    );
  }, [str]);

  useEffect(() => {
    if (!str) {
      setMdValue('');
      return;
    }
    if (!hasDynamicMarkdown) {
      renderMd();
      return;
    }
    // Coalesce burst updates while items are batch-loaded.
    const timeoutId = window.setTimeout(() => {
      renderMd();
    }, 80);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [str, hasDynamicMarkdown, item, items]);

  const updateText = value => {
    if (!canEditItemForItem(item, items)) {
      return;
    }
    const clonedTexts = deepCloneArray(item.text);
    const idx = findIndexOfObjectInArray(clonedTexts, textId);
    clonedTexts[idx][textId].text = value;
    update(`items/${itemId}`, 'text', clonedTexts);
  };

  const updateMdStr = attribs => {
    if (isEditable() === false || !canEditItemForItem(item, items)) return;

    let clonedAttrib = attribs['data-raw']
      ? decodeCheckboxAttrValue(attribs['data-raw'])
      : '';

    if (!clonedAttrib) {
      let legacyContent = decodeCheckboxAttrValue(attribs['data-content']);
      for (let i = 0; i < mdRules.length; i++) {
        const rule = mdRules[i];
        legacyContent = legacyContent.replace(
          rule.reversePattern,
          rule.reverseReplacement
        );
      }
      clonedAttrib = legacyContent;
    }

    if (!clonedAttrib) {
      return;
    }

    let clonedValue = str;
    if (attribs.ischecked === 'false') {
      clonedValue = str.replace(`[] ${clonedAttrib}`, `[x] ${clonedAttrib}`);
    }
    if (attribs.ischecked === 'true') {
      clonedValue = str.replace(`[x] ${clonedAttrib}`, `[] ${clonedAttrib}`);
    }
    updateText(clonedValue);
  };

  const updateMdSelect = (attribs, nextValue, selectIndex) => {
    if (isEditable() === false || !canEditItemForItem(item, items)) return;
    const tokenBodyRaw = decodeSelectAttrValue(attribs?.['data-token']);
    if (!tokenBodyRaw) {
      return;
    }
    const optionsRaw = decodeSelectAttrValue(attribs?.['data-options']);
    let options = [];
    try {
      options = JSON.parse(optionsRaw);
    } catch (error) {
      options = [];
    }
    if (!Array.isArray(options) || options.length === 0) {
      return;
    }
    const normalizedNextValue = `${nextValue ?? ''}`;
    if (!normalizedNextValue) {
      return;
    }
    const nextTokenBody = `${normalizedNextValue}|${options.join('|')}`;
    const prevToken = `{{select:${tokenBodyRaw}}}`;
    const nextToken = `{{select:${nextTokenBody}}}`;

    // Prefer index-based replacement: fixes the case where multiple selects have the same token body.
    const updatedByIndex = replaceNthMdSelectToken(
      str,
      selectIndex,
      normalizedNextValue,
      tokenBodyRaw
    );
    if (updatedByIndex !== str) {
      updateText(updatedByIndex);
      return;
    }

    // Fallback to legacy behavior (best effort).
    if (!str?.includes(prevToken)) {
      return;
    }
    updateText(str.replace(prevToken, nextToken));
  };

  // Reset per render of this TextMd; `options.replace` will increment as it encounters select nodes.
  let mdSelectIndex = 0;
  const options = {
    replace: domNode => {
      const { attribs, children } = domNode;
      if (domNode?.name === 'script' || domNode?.name === 'style') {
        return <></>;
      }
      if (!attribs) {
        return;
      }

      if (attribs['data-icon']) {
        const slug = attribs['data-icon'];
        const iconElement = renderIconBySlug(slug);
        if (!iconElement) {
          return <span className='md-icon-fallback'>{`:${slug}:`}</span>;
        }
        return <span className='md-icon'>{iconElement}</span>;
      }

      if (attribs.type === 'checkbox') {
        const labelHtml = decodeCheckboxAttrValue(attribs['data-content']);
        return (
          <Checkbox
            label={parse(labelHtml, options)}
            value={attribs.ischecked === 'true'}
            onChange={() => updateMdStr(attribs)}
            size='sm'
            className='w-full items-start'
            labelClassName='whitespace-normal break-words'
            truncateLabel={false}
          />
        );
      }

      if (attribs['data-md-select'] === 'true') {
        const selectIndex = mdSelectIndex;
        mdSelectIndex += 1;

        const current = decodeSelectAttrValue(attribs['data-current']);
        const optionsRaw = decodeSelectAttrValue(attribs['data-options']);
        let selectOptions = [];
        try {
          selectOptions = JSON.parse(optionsRaw);
        } catch (error) {
          selectOptions = [];
        }
        if (!Array.isArray(selectOptions) || selectOptions.length === 0) {
          return null;
        }
        const selectedValue = selectOptions.includes(current)
          ? current
          : selectOptions[0];
        return (
          <div
            className='board-grid-no-drag inline-flex align-middle min-w-120'
            onClick={event => event.stopPropagation()}
          >
            <SettingsSelect
              value={selectedValue}
              size='sm'
              disabled={!canEditItemForItem(item, items)}
              options={selectOptions.map(option => ({
                value: option,
                label: option,
              }))}
              onChange={event =>
                updateMdSelect(attribs, event.target.value, selectIndex)
              }
            />
          </div>
        );
      }

      if (attribs.id === 'item-link') {
        if (isEditable()) {
          return (
            <TooltipHtml
              title={
                <div
                  className='overflow-hidden rounded shadow-md'
                  style={{
                    maxHeight: 300,
                    width: 300,
                    marginTop: -4,
                    marginBottom: -4,
                    marginLeft: -8,
                  }}
                >
                  <ItemShort
                    itemId={attribs.href}
                    displayType={'tooltip'}
                    height={300}
                  />
                </div>
              }
            >
              <span
                onClick={() => dispatch(setDrawerOpen(attribs.href))}
                className={attribs.class}
              >
                {domToReact(children, options)}
              </span>
            </TooltipHtml>
          );
        } else {
          return (
            <span
              onClick={() => dispatch(setDrawerOpen(attribs.href))}
              className={attribs.class}
            >
              {domToReact(children, options)}
            </span>
          );
        }
      }
    },
  };

  const ref = useRef(null);
  const lastNotifiedHeightRef = useRef(0);
  const canEdit = canEditItemForItem(item, items);

  const notifyTextHeight = useCallback(() => {
    if (!getTextHeight || !ref.current) {
      return;
    }
    const measuredHeight = ref.current.getBoundingClientRect?.().height ?? 0;
    if (measuredHeight > 0) {
      // Prevent noisy resize loops caused by sub-pixel fluctuations.
      if (Math.abs(measuredHeight - lastNotifiedHeightRef.current) < 1) {
        return;
      }
      lastNotifiedHeightRef.current = measuredHeight;
      getTextHeight(measuredHeight, textId, itemId, 'text', gridLayoutKey);
    }
  }, [getTextHeight, textId, itemId, gridLayoutKey]);

  useEffect(() => {
    notifyTextHeight();

    let rafA = null;
    let rafB = null;
    if (typeof window !== 'undefined') {
      rafA = window.requestAnimationFrame(() => {
        notifyTextHeight();
        rafB = window.requestAnimationFrame(() => {
          notifyTextHeight();
        });
      });
    }

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined' && ref.current) {
      resizeObserver = new ResizeObserver(() => {
        notifyTextHeight();
      });
      resizeObserver.observe(ref.current);
    }

    return () => {
      if (rafA) {
        window.cancelAnimationFrame(rafA);
      }
      if (rafB) {
        window.cancelAnimationFrame(rafB);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [notifyTextHeight, mdValue, windowSize?.width, windowSize?.height, boardTabId]);
  const bind = useDoubleTap(event => {
    if (isEditable() && canEdit) {
      dispatch(setOpenModales({ id: textId + itemId, open: true }));
    }
  });
  const isEmptyText = typeof str === 'string' && str.trim() === '';
  return (
    <div
      {...bind}
      ref={ref}
      className={`w-full ${isEmptyText ? 'min-h-textEmpty' : ''}`}
      style={style}
    >
      {isEmptyText ? (
        <span className='block w-full min-h-textEmpty opacity-0 select-none'>
          texte vide
        </span>
      ) : (
        parse(mdValue, options)
      )}
    </div>
  );
};
